import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { Check, ChevronDown, Columns3, Download } from 'lucide-react';
import { cn } from './cn';
import { EmptyState, ErrorState, TableSkeleton } from './States';
import { Pagination } from './Pagination';

export interface Column<T> {
  key: string;
  header: ReactNode;
  /** Cell renderer. Given the row, so a cell can combine fields. */
  cell: (row: T) => ReactNode;
  className?: string;
  /** Right-aligned for counts, amounts and dates; left for everything else. */
  align?: 'left' | 'right';
  /** Marks the column sortable and names the field the caller sorts by. */
  sortKey?: string;
  /** Hidden below `md`. Use for columns a phone genuinely cannot carry. */
  hideOnMobile?: boolean;
  /**
   * On a phone's stacked row: `true` puts this field behind the row's
   * "Details" button, `false` keeps it in view. Left unset, a row with more
   * than four labelled fields keeps its first three in view and folds the
   * rest — a card that lists eight fields is a form, and five of them push
   * the next worker off the screen.
   * A desktop table never reads this.
   */
  detail?: boolean;
  /**
   * The label a stacked row gives this cell on a phone, when the header is
   * not plain text or says something different out of a table's context.
   * `false` shows the cell without a label — for a cell that already says
   * what it is, like a status badge.
   */
  mobileLabel?: string | false;
  /**
   * Kept out of the column-visibility menu and never hideable.
   *
   * For the column that identifies the row — a worker's name, an invoice
   * number. A table whose identifying column has been switched off is a grid
   * of values belonging to nobody.
   */
  /** On a stacked row, take the full width rather than one of two columns. */
  stackFull?: boolean;
  /**
   * Width this column needs before its cell breaks into a tall stack, in px.
   * Default 120 for a named column. On a touch screen a table whose columns
   * need more than the frame has becomes stacked cards.
   */
  minWidth?: number;
  alwaysVisible?: boolean;
  /** Hidden by default, offered in the menu. For the long tail of detail. */
  defaultHidden?: boolean;
}

export interface SortState {
  key: string;
  direction: 'asc' | 'desc';
}

/**
 * A dense table with the states built in.
 *
 * Loading, empty and error are parameters rather than something each caller
 * remembers to handle — the definition of done requires all three on every
 * screen, and the reliable way to get that is to make the blank case
 * impossible to reach by accident.
 *
 * Pagination is cursor-based by design. OFFSET on a live worklist duplicates
 * and skips rows while check-ins are happening, which in a clinical context
 * means somebody is tested twice or not at all.
 *
 * The wrapper scrolls horizontally rather than the page: a wide table on a
 * laptop should move sideways inside its own frame, not drag the navigation
 * off the left of the screen with it.
 */
export function DataTable<T>({
  columns, rows, loading, error, empty, onRetry, rowKey, onLoadMore, hasMore,
  loadingMore, sort, onSortChange, onRowClick, dense, className,
  selectable, selected, onSelectedChange, bulkActions,
  onExport, exportLabel = 'Export', columnsKey, total,
  numbered = true, rowNumberStart = 1, pageSize = 25,
}: {
  columns: Array<Column<T>>;
  rows: T[];
  loading?: boolean;
  error?: string | null;
  empty?: { title: string; hint?: string; action?: ReactNode; icon?: ReactNode };
  onRetry?: () => void;
  rowKey: (row: T) => string;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  sort?: SortState;
  onSortChange?: (s: SortState) => void;
  onRowClick?: (row: T) => void;
  dense?: boolean;
  className?: string;
  /** Adds a checkbox column and the bulk bar above it. */
  selectable?: boolean;
  selected?: ReadonlySet<string>;
  onSelectedChange?: (next: Set<string>) => void;
  /** Rendered in the bulk bar. Given the selected keys. */
  bulkActions?: (keys: string[]) => ReactNode;
  /**
   * Offered only when the caller passes it — which is how "export where
   * authorized" stays a permission decision on the screen rather than a
   * button this component decides to render.
   */
  onExport?: () => void | Promise<void>;
  exportLabel?: string;
  /**
   * Remembers which columns are showing, per table.
   *
   * Somebody who works all day in one table hides three columns once and
   * should not do it again tomorrow. Omit it and the choice lasts for the
   * visit only.
   */
  columnsKey?: string;
  /** Server-side total, when the API knows it. Shown beside the count. */
  total?: number;
  /**
   * A "#" column (and the number on each phone card), 1, 2, 3… — so a
   * person can say "row 14" (owner, 29 Oct 2026). On by default.
   */
  numbered?: boolean;
  /** The number of the first row, for a list paged by the caller (page 2 of 25 starts at 26). */
  rowNumberStart?: number;
  /**
   * Rows per page when the caller does not page the list itself (no
   * `onLoadMore`): longer lists get a pager instead of one endless page.
   * 0 turns it off.
   */
  pageSize?: number;
}) {
  const hideable = columns.filter((c) => !c.alwaysVisible);
  const [hidden, setHidden] = useHiddenColumns(columnsKey, columns);
  // Before any early return: a hook after one runs on some renders only.
  // Callback refs held in state: the frame appears only once loading ends,
  // and the effects that watch it must run again when it does.
  const [card, setCard] = useState<HTMLDivElement | null>(null);
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  // Room the columns need before their cells start breaking into three-line
  // stacks: a named column ~120px, an unnamed one (an action, a chevron) less.
  const wants = columns.filter((c) => !hidden.has(c.key))
    .reduce((w, c) => w + (c.minWidth ?? (headerText(c.header) ? 120 : 56)), (selectable ? 40 : 0) + (numbered ? 48 : 0));
  const cramped = useCramped(card, scroller, wants);
  const stacked = useNarrow() || cramped;
  const visible = columns.filter((c) => !hidden.has(c.key));

  // Paged here when the caller does not page: a list of 140 invoices is six
  // pages of 25, not one page a tablet has to scroll for a minute.
  const [pageIdx, setPageIdx] = useState(0);
  const clientPaged = !onLoadMore && pageSize > 0 && rows.length > pageSize;
  const pageCount = clientPaged ? Math.ceil(rows.length / pageSize) : 1;
  const page = Math.min(pageIdx, pageCount - 1);
  // A new filter or search is a new list: back to its first page.
  useEffect(() => { setPageIdx(0); }, [rows.length]);
  const pageRows = clientPaged ? rows.slice(page * pageSize, (page + 1) * pageSize) : rows;
  const firstNumber = rowNumberStart + (clientPaged ? page * pageSize : 0);

  const allKeys = rows.map(rowKey);
  const selectedKeys = selected ?? EMPTY_SET;
  const selectedHere = allKeys.filter((k) => selectedKeys.has(k));
  const allSelected = allKeys.length > 0 && selectedHere.length === allKeys.length;

  function toggleRow(key: string) {
    const next = new Set(selectedKeys);
    if (next.has(key)) next.delete(key); else next.add(key);
    onSelectedChange?.(next);
  }

  function toggleAll() {
    const next = new Set(selectedKeys);
    if (allSelected) for (const k of allKeys) next.delete(k);
    else for (const k of allKeys) next.add(k);
    onSelectedChange?.(next);
  }

  if (error) return <ErrorState detail={error} onRetry={onRetry} />;
  if (loading) {
    return (
      <div className={cn('overflow-hidden rounded-card border border-neutral-200 bg-white', className)}>
        <TableSkeleton cols={visible.length} />
      </div>
    );
  }
  if (rows.length === 0 && empty) return <EmptyState {...empty} />;

  const rowHeight = dense ? 'h-11' : 'h-14';
  const showChrome = Boolean(onExport || (columnsKey && hideable.length > 1) || total !== undefined);

  return (
    <div ref={setCard} className={cn('overflow-hidden rounded-card border border-neutral-200 bg-white shadow-card', className)}>
      {/* The bulk bar replaces the chrome rather than stacking on it: while a
          selection is live, that is the only thing the reader is doing. */}
      {selectable && selectedHere.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-primary-100 bg-primary-50 px-4 py-2">
          <span className="text-small font-semibold text-primary-700">
            {selectedKeys.size} selected
          </span>
          <button
            type="button"
            onClick={() => onSelectedChange?.(new Set())}
            className="text-caption font-medium text-primary-600 hover:underline"
          >
            Clear
          </button>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {bulkActions?.([...selectedKeys])}
          </div>
        </div>
      ) : showChrome && (
        <div className="flex flex-wrap items-center gap-2 border-b border-neutral-100 px-4 py-2">
          {total !== undefined && (
            <span className="text-caption text-neutral-500">
              {/* Says what is on screen AND what exists, because a table that
                  shows 50 of 4,000 and reports "50" is quietly lying. */}
              Showing {rows.length}{total > rows.length ? ` of ${total}` : ''}
            </span>
          )}
          <div className="ml-auto flex items-center gap-1.5">
            {columnsKey && hideable.length > 1 && (
              <ColumnMenu
                columns={hideable}
                hidden={hidden}
                onToggle={(key) => setHidden((prev) => {
                  const next = new Set(prev);
                  if (next.has(key)) next.delete(key); else next.add(key);
                  return next;
                })}
              />
            )}
            {onExport && (
              <button
                type="button"
                onClick={() => onExport()}
                className="inline-flex h-8 max-md:h-10 items-center gap-1.5 rounded-btn border border-neutral-300 px-2.5 text-caption font-medium text-neutral-700 hover:bg-neutral-50"
              >
                <Download className="h-3.5 w-3.5" /> {exportLabel}
              </button>
            )}
          </div>
        </div>
      )}
      {stacked ? (
        <StackedRows
          columns={visible.filter((c) => !c.hideOnMobile)}
          rows={pageRows}
          firstNumber={numbered ? firstNumber : null}
          paged={clientPaged}
          rowKey={rowKey}
          onRowClick={onRowClick}
          sort={sort}
          onSortChange={onSortChange}
          selectable={selectable}
          selectedKeys={selectedKeys}
          onToggleRow={toggleRow}
        />
      ) : (
      <div ref={setScroller} className="overflow-x-auto">
        <table className="w-full border-collapse">
          {/* Sticky, because a fifty-row worklist scrolled halfway down is a
              grid of numbers whose columns nobody can name any more. */}
          <thead className="sticky top-0 z-10 bg-neutral-50">
            <tr className="border-b border-neutral-200">
              {numbered && (
                <th scope="col" className="w-12 py-2.5 pl-4 pr-1 text-left text-caption font-semibold uppercase tracking-wide text-neutral-500">
                  <span aria-hidden>#</span><span className="sr-only">Row number</span>
                </th>
              )}
              {selectable && (
                <th scope="col" className="w-10 px-4 py-2.5">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    // Neither all nor none: the box says "some", rather than
                    // reading as empty and inviting a click that clears work.
                    ref={(el) => {
                      if (el) el.indeterminate = selectedHere.length > 0 && !allSelected;
                    }}
                    onChange={toggleAll}
                    aria-label={allSelected ? 'Clear selection' : 'Select all loaded rows'}
                    className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
              )}
              {visible.map((c) => {
                const sortable = Boolean(c.sortKey && onSortChange);
                // Both sides must exist. `sort?.key === c.sortKey` is true
                // when BOTH are undefined, which marked every non-sortable
                // column as actively sorted and then read `.direction` off
                // nothing — a white screen on any table without sorting.
                const active = sortable && sort?.key === c.sortKey;
                return (
                  <th
                    key={c.key}
                    scope="col"
                    aria-sort={active ? (sort!.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                    className={cn(
                      'px-3 py-2.5 text-caption font-semibold uppercase tracking-wide text-neutral-600 xl:px-4',
                      c.align === 'right' ? 'text-right' : 'text-left',
                      c.hideOnMobile && 'hidden md:table-cell',
                      c.className,
                    )}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() => onSortChange!({
                          key: c.sortKey!,
                          direction: active && sort!.direction === 'asc' ? 'desc' : 'asc',
                        })}
                        className={cn(
                          // A button does not inherit text-transform, so a sortable
                          // heading read "Coverage" beside an unsortable "NEXT STEP".
                          'inline-flex items-center gap-1 rounded uppercase hover:text-neutral-900',
                          c.align === 'right' && 'flex-row-reverse',
                        )}
                      >
                        {c.header}
                        <span aria-hidden className={cn('text-[9px]', active ? 'text-primary-600' : 'text-neutral-400')}>
                          {active ? (sort!.direction === 'asc' ? '▲' : '▼') : '▲▼'}
                        </span>
                      </button>
                    ) : c.header}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {pageRows.map((row, i) => (
              <tr
                key={rowKey(row)}
                data-selected={selectable && selectedKeys.has(rowKey(row)) ? '' : undefined}
                {...(onRowClick ? {
                  onClick: () => onRowClick(row),
                  tabIndex: 0,
                  // A row that responds to a click must respond to Enter, or
                  // the screen is unusable without a mouse.
                  onKeyDown: (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row); }
                  },
                } : {})}
                className={cn(
                  rowHeight,
                  'border-b border-neutral-100 last:border-0',
                  'transition-colors duration-hover hover:bg-neutral-50',
                  onRowClick && 'cursor-pointer focus-visible:bg-primary-50 focus-visible:outline-none',
                  'data-[selected]:bg-primary-50/60',
                )}
              >
                {numbered && (
                  <td className="w-12 py-2 pl-4 pr-1 text-caption tabular-nums text-neutral-500">{firstNumber + i}</td>
                )}
                {selectable && (
                  <td className="w-10 px-4">
                    <input
                      type="checkbox"
                      checked={selectedKeys.has(rowKey(row))}
                      // Selecting a row must not also open it.
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleRow(rowKey(row))}
                      aria-label="Select row"
                      className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                    />
                  </td>
                )}
                {visible.map((c) => (
                  <td
                    key={c.key}
                    className={cn(
                      'px-3 text-body text-neutral-700 xl:px-4',
                      c.align === 'right' ? 'text-right' : 'text-left',
                      c.hideOnMobile && 'hidden md:table-cell',
                      c.className,
                    )}
                  >
                    {c.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {clientPaged && (
        <div className="border-t border-neutral-100 px-4 py-2.5">
          <Pagination page={page + 1} pageSize={pageSize} total={rows.length} onPageChange={(n) => setPageIdx(n - 1)} />
        </div>
      )}

      {hasMore && onLoadMore && (
        <div className="border-t border-neutral-100 p-3 text-center">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loadingMore}
            className="rounded-btn px-3 py-1.5 text-small font-medium text-primary-600 hover:bg-primary-50 disabled:text-neutral-500 max-md:min-h-10 [@media(pointer:coarse)]:min-h-10"
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * A toolbar for the controls that sit above a table.
 *
 * Exists so search, filters and the primary action land in the same place on
 * every screen. A filter that moves between pages is one the reader has to
 * find again each time.
 */
export function TableToolbar({ children, actions, className }: {
  children?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {children}
      {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
    </div>
  );
}


const EMPTY_SET: ReadonlySet<string> = new Set();

/**
 * Below `md` — a phone, or a tablet held upright in a narrow split view.
 *
 * A subscription rather than a CSS switch between two renderings: rendering
 * the rows twice and hiding one would put every button in the DOM twice, and
 * a screen reader, a test or a drive that looks for "Review" would find the
 * invisible one.
 */
const NARROW = '(max-width: 767.98px)';
function subscribeNarrow(cb: () => void) {
  if (typeof window === 'undefined' || !window.matchMedia) return () => undefined;
  const mq = window.matchMedia(NARROW);
  mq.addEventListener('change', cb);
  return () => mq.removeEventListener('change', cb);
}
/** True below `md`: the width at which a table becomes stacked rows. */
export function useNarrow(): boolean {
  return useSyncExternalStore(
    subscribeNarrow,
    () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(NARROW).matches : false),
    () => false,
  );
}

/** A touch screen as the main pointer: a tablet, whatever its width. */
const COARSE = '(pointer: coarse)';
function subscribeCoarse(cb: () => void) {
  if (typeof window === 'undefined' || !window.matchMedia) return () => undefined;
  const mq = window.matchMedia(COARSE);
  mq.addEventListener('change', cb);
  return () => mq.removeEventListener('change', cb);
}
function useCoarse(): boolean {
  return useSyncExternalStore(
    subscribeCoarse,
    () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(COARSE).matches : false),
    () => false,
  );
}

/**
 * True on a touch screen when the table's columns do not fit its frame.
 *
 * A mouse can drag a wide table sideways; on a tablet nothing says the last
 * column is there, so a table that would scroll becomes stacked rows
 * instead. The width the columns needed is kept, so turning the tablet to
 * landscape brings the table back once it fits.
 */
function useCramped(card: HTMLDivElement | null, scroller: HTMLDivElement | null, wants: number): boolean {
  const coarse = useCoarse();
  const [measured, setNeeded] = useState<number | null>(null);
  const [width, setWidth] = useState<number | null>(null);
  // Too narrow for the columns, even where nothing overflows yet.
  const squeezed = coarse && width !== null && width < wants;
  const needed = measured ?? (squeezed ? wants : null);
  const cramped = coarse && needed !== null;

  // The frame's width, whichever way the rows are drawn.
  useLayoutEffect(() => {
    const el = card;
    if (!coarse || !el) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, [card, coarse]);

  // Table showing: does it fit? Runs after each render, since new rows can widen it.
  useLayoutEffect(() => {
    const el = scroller;
    if (!coarse || cramped || !el) return;
    if (el.scrollWidth > el.clientWidth + 1) setNeeded(el.scrollWidth);
  });

  // Stacked: watch the frame and give the table back once there is room.
  useEffect(() => {
    const el = card;
    if (!cramped || !el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (measured !== null && el.clientWidth >= Math.max(measured, wants)) setNeeded(null);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [card, cramped, measured, wants]);

  // Table showing: a frame that shrinks (rotation, a sidebar) is checked again.
  useEffect(() => {
    const el = scroller;
    if (!coarse || cramped || !el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (el.scrollWidth > el.clientWidth + 1) setNeeded(el.scrollWidth);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [scroller, coarse, cramped]);

  return cramped;
}

/** Plain text of a header, for a label or an option. */
function headerText(h: ReactNode): string {
  return typeof h === 'string' || typeof h === 'number' ? String(h) : '';
}

/** Labelled fields a stacked row shows before its "Details" button. */
const KEY_FIELDS = 3;
/** Stacked rows drawn at a time on a phone. */
const PHONE_PAGE = 20;

/**
 * A table's rows as a stack of cards, on a screen too narrow for columns.
 *
 * A seven-column worklist scrolled sideways on a phone shows the name and
 * hides everything that tells the reader what to do about it. Stacked, the
 * first column — the one that identifies the row — is the heading, every
 * other column is a labelled line under it, and a column with no header
 * (the row's action, a chevron) sits at the foot of the card where a thumb
 * reaches it.
 */
function StackedRows<T>({
  columns, rows, rowKey, onRowClick, sort, onSortChange, selectable, selectedKeys, onToggleRow, firstNumber, paged,
}: {
  columns: Array<Column<T>>;
  rows: T[];
  /** The number of the first card, or null for none. */
  firstNumber: number | null;
  /** The table already pages: no "show more" on top of it. */
  paged: boolean;
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  sort?: SortState;
  onSortChange?: (s: SortState) => void;
  selectable?: boolean;
  selectedKeys: ReadonlySet<string>;
  onToggleRow: (key: string) => void;
}) {
  const [head, ...rest] = columns;
  const labelOf = (c: Column<T>) => (c.mobileLabel === false ? '' : c.mobileLabel ?? headerText(c.header));
  const body = rest.filter((c) => labelOf(c) || c.mobileLabel === false);
  const foot = rest.filter((c) => !labelOf(c) && c.mobileLabel !== false);
  const sortable = onSortChange ? columns.filter((c) => c.sortKey && headerText(c.header)) : [];

  // What a card shows before "Details": every unlabelled cell (a status
  // badge says what it is) and the first KEY_FIELDS labelled ones, unless a
  // column says otherwise.
  // A card with four labelled fields shows all four: a "Details" button that
  // opens one more line is a tap for nothing.
  const foldByDefault = body.filter((c) => c.detail === undefined && labelOf(c)).length > KEY_FIELDS + 1;
  let labelled = 0;
  const folded = new Set<string>();
  for (const c of body) {
    const fold = c.detail ?? (labelOf(c) ? ++labelled > KEY_FIELDS && foldByDefault : false);
    if (fold) folded.add(c.key);
  }
  const [openRows, setOpenRows] = useState<ReadonlySet<string>>(new Set());
  const toggleDetails = (key: string) => setOpenRows((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // A long list is paged on a phone: seventy stacked cards are fourteen
  // thousand pixels of thumb.
  const [shown, setShown] = useState(PHONE_PAGE);
  const visibleRows = !paged && rows.length > shown ? rows.slice(0, shown) : rows;

  return (
    <div>
      {sortable.length > 0 && (
        <div className="flex items-center gap-2 border-b border-neutral-100 px-4 py-2">
          <label className="text-caption text-neutral-600" htmlFor="dt-sort">Sort by</label>
          <select
            id="dt-sort"
            value={sort ? `${sort.key}:${sort.direction}` : ''}
            onChange={(e) => {
              const [key, direction] = e.target.value.split(':');
              if (key) onSortChange!({ key, direction: direction as SortState['direction'] });
            }}
            className="h-10 min-w-0 flex-1 rounded-input border border-neutral-300 bg-white px-2 text-small text-neutral-800"
          >
            {!sort && <option value="">Default order</option>}
            {sortable.flatMap((c) => [
              <option key={`${c.key}a`} value={`${c.sortKey}:asc`}>{headerText(c.header)} ↑</option>,
              <option key={`${c.key}d`} value={`${c.sortKey}:desc`}>{headerText(c.header)} ↓</option>,
            ])}
          </select>
        </div>
      )}
      <ul className="divide-y divide-neutral-100">
        {visibleRows.map((row, idx) => {
          const key = rowKey(row);
          const detailsOpen = openRows.has(key);
          return (
            <li
              key={key}
              data-selected={selectable && selectedKeys.has(key) ? '' : undefined}
              {...(onRowClick ? {
                onClick: () => onRowClick(row),
                tabIndex: 0,
                onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.target !== e.currentTarget) return;
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row); }
                },
              } : {})}
              className={cn(
                'flex gap-3 px-4 py-3',
                onRowClick && 'cursor-pointer hover:bg-neutral-50 focus-visible:bg-primary-50 focus-visible:outline-none',
                'data-[selected]:bg-primary-50/60',
              )}
            >
              {selectable && (
                <input
                  type="checkbox"
                  checked={selectedKeys.has(key)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => onToggleRow(key)}
                  aria-label="Select row"
                  className="mt-1 h-5 w-5 shrink-0 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                />
              )}
              <div className="min-w-0 flex-1">
                {/* The row's action (a chevron, "Review") sits beside the
                    heading rather than on a line of its own at the foot,
                    where it left a strip of empty card under every row. */}
                <div className="flex items-start gap-3">
                  {firstNumber !== null && (
                    <span className="mt-0.5 flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-neutral-100 px-1.5 text-micro tabular-nums text-neutral-600">
                      {firstNumber + idx}
                    </span>
                  )}
                  {head && <div className="min-w-0 flex-1 break-words text-body text-neutral-900">{head.cell(row)}</div>}
                  {foot.length > 0 && (
                    <div className="flex shrink-0 items-center gap-2">
                      {foot.map((c) => <div key={c.key}>{c.cell(row)}</div>)}
                    </div>
                  )}
                </div>
                {/* Unlabelled cells say what they are (a status badge): one
                    row of them under the heading, not each alone on a line
                    among the labelled fields. */}
                {body.some((c) => !labelOf(c) && (detailsOpen || !folded.has(c.key))) && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-small">
                    {body.filter((c) => !labelOf(c) && (detailsOpen || !folded.has(c.key))).map((c) => (
                      <div key={c.key} className="min-w-0">{c.cell(row)}</div>
                    ))}
                  </div>
                )}
                {body.some((c) => labelOf(c)) && (
                  <dl className="mt-1.5 space-y-1 sm:grid sm:grid-cols-2 sm:gap-x-6 sm:gap-y-1.5 sm:space-y-0">
                    {body.filter((c) => labelOf(c) && (detailsOpen || !folded.has(c.key))).map((c) => {
                      const label = labelOf(c);
                      return (
                        <div key={c.key} className={cn('flex min-w-0 items-baseline gap-2 text-small', c.stackFull && 'sm:col-span-2')}>
                          {label && <dt className="w-28 shrink-0 text-caption font-normal text-neutral-500">{label}</dt>}
                          <dd className="min-w-0 flex-1 break-words text-neutral-700">{c.cell(row)}</dd>
                        </div>
                      );
                    })}
                  </dl>
                )}
                {folded.size > 0 && (
                  <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
                    {folded.size > 0 && (
                      <button
                        type="button"
                        aria-expanded={detailsOpen}
                        // The card itself opens the record; this only unfolds it.
                        onClick={(e) => { e.stopPropagation(); toggleDetails(key); }}
                        className="-ml-2 mr-auto inline-flex h-10 items-center gap-1 rounded-btn px-2 text-small font-medium text-primary-600"
                      >
                        {detailsOpen ? 'Hide details' : 'Details'}
                        <ChevronDown aria-hidden className={cn('h-4 w-4 transition-transform', detailsOpen && 'rotate-180')} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {rows.length > visibleRows.length && (
        <div className="border-t border-neutral-100 p-3">
          <button
            type="button"
            onClick={() => setShown((n) => n + PHONE_PAGE)}
            className="inline-flex h-11 w-full items-center justify-center rounded-btn border border-neutral-300 bg-white text-small font-semibold text-neutral-800"
          >
            Show {Math.min(PHONE_PAGE, rows.length - visibleRows.length)} more
            {rows.length - visibleRows.length > PHONE_PAGE && (
              <span className="ml-1 font-normal text-neutral-500">· {rows.length - visibleRows.length} not shown</span>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Which columns are hidden, remembered per table.
 *
 * Falls back to the in-memory default whenever storage is unavailable — a
 * private window, or a browser set to block site data. A table that throws
 * because it could not remember a column preference is worse than one that
 * forgets.
 */
function useHiddenColumns<T>(
  key: string | undefined,
  columns: Array<Column<T>>,
): [Set<string>, (fn: (prev: Set<string>) => Set<string>) => void] {
  const initial = () => {
    const byDefault = new Set(columns.filter((c) => c.defaultHidden).map((c) => c.key));
    if (!key) return byDefault;
    try {
      const raw = localStorage.getItem(`dru.cols.${key}`);
      if (!raw) return byDefault;
      const stored = new Set<string>(JSON.parse(raw));
      // A column that has since been marked alwaysVisible must come back,
      // rather than staying hidden because an old preference said so.
      for (const c of columns) if (c.alwaysVisible) stored.delete(c.key);
      return stored;
    } catch {
      return byDefault;
    }
  };

  const [hidden, setHidden] = useState<Set<string>>(initial);

  useEffect(() => {
    if (!key) return;
    try {
      localStorage.setItem(`dru.cols.${key}`, JSON.stringify([...hidden]));
    } catch {
      // Not worth surfacing: the table works, it just will not remember.
    }
  }, [key, hidden]);

  return [hidden, setHidden];
}

function ColumnMenu<T>({ columns, hidden, onToggle }: {
  columns: Array<Column<T>>;
  hidden: ReadonlySet<string>;
  onToggle: (key: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    function onDocDown(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const shown = columns.length - [...hidden].filter((k) => columns.some((c) => c.key === k)).length;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        className="inline-flex h-8 max-md:h-10 items-center gap-1.5 rounded-btn border border-neutral-300 px-2.5 text-caption font-medium text-neutral-700 hover:bg-neutral-50"
      >
        <Columns3 className="h-3.5 w-3.5" />
        Columns
        {shown < columns.length && (
          <span className="rounded-badge bg-primary-100 px-1 text-micro text-primary-700">
            {shown}/{columns.length}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-1 max-h-80 w-56 overflow-y-auto rounded-card border border-neutral-200 bg-white py-1 shadow-dropdown animate-fade-in"
        >
          {columns.map((c) => {
            const isHidden = hidden.has(c.key);
            return (
              <button
                key={c.key}
                type="button"
                role="menuitemcheckbox"
                aria-checked={!isHidden}
                onClick={() => onToggle(c.key)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-small text-neutral-700 hover:bg-neutral-50"
              >
                <Check className={cn('h-3.5 w-3.5 shrink-0',
                  isHidden ? 'text-transparent' : 'text-primary-600')} />
                <span className="truncate">{c.header}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
